import type { Lang } from './categories';

export interface UiContent {
  nav: {
    categories: string;
    guides: string;
    reviews: string;
    shipping: string;
    warehouse: string;
    qc: string;
    faq: string;
    cta: string;
    home: string;
    shoppingGuide: string;
  };
  categoryLabel: string;
  mainCta: string;
  buyNow: string;
  readMore: string;
  backHome: string;
  relatedResources: string;
  exploreResources: string;
  visitMain: string;
  breadcrumbHome: string;
  published: string;
  articleDisclaimer: string;
  searchPlaceholder: string;
  categoryLabels: Record<string, string>;
  footer: {
    about: string;
    contact: string;
    editorial: string;
    disclaimer: string;
    affiliate: string;
    privacy: string;
    terms: string;
    sitemap: string;
    disclaimerText: string;
    copyright: string;
    visitMain: string;
    explore: string;
  };
}

export const uiContent: Record<Lang, UiContent> = {
  en: {
    nav: {
      categories: 'Categories',
      guides: 'Guides',
      reviews: 'Reviews',
      shipping: 'Shipping',
      warehouse: 'Warehouse',
      qc: 'QC',
      faq: 'FAQ',
      cta: 'Explore',
      home: 'Home',
      shoppingGuide: 'Shopping Guide',
    },
    categoryLabel: 'Browse Categories',
    mainCta: 'AllChinaBuy Spreadsheet',
    buyNow: 'Buy It Now',
    readMore: 'Read More',
    backHome: 'Back to Home',
    relatedResources: 'Related Resources',
    exploreResources: 'Explore More AllChinaBuy Resources',
    visitMain: 'Visit Main Website',
    breadcrumbHome: 'Home',
    published: 'Updated',
    articleDisclaimer: 'This article is for informational purposes only and is not affiliated with or endorsed by AllChinaBuy.',
    searchPlaceholder: 'Search products, sellers, guides or spreadsheets...',
    categoryLabels: {
      spreadsheet: 'Spreadsheet',
      review: 'Review',
      shipping: 'Shipping',
      coupon: 'Coupon',
      warehouse: 'Warehouse',
      trust: 'Trust & Safety',
      qc: 'QC',
      alternatives: 'Alternatives',
      comparison: 'Comparison',
      guide: 'Guide',
      sellers: 'Sellers',
      products: 'Products',
    },
    footer: {
      about: 'About',
      contact: 'Contact',
      editorial: 'Editorial Policy',
      disclaimer: 'Disclaimer',
      affiliate: 'Affiliate Disclosure',
      privacy: 'Privacy Policy',
      terms: 'Terms',
      sitemap: 'Sitemap',
      disclaimerText: 'This website is an independent informational resource and is not affiliated with or officially endorsed by AllChinaBuy. Always verify current product, seller, payment and shipping information before making a purchase.',
      copyright: 'All rights reserved.',
      visitMain: 'Visit Main Website',
      explore: 'Explore Resources',
    },
  },
  fr: {
    nav: {
      categories: 'Catégories',
      guides: 'Guides',
      reviews: 'Avis',
      shipping: 'Livraison',
      warehouse: 'Entrepôt',
      qc: 'QC',
      faq: 'FAQ',
      cta: 'Explorer',
      home: 'Accueil',
      shoppingGuide: "Guide d'achat",
    },
    categoryLabel: 'Parcourir les catégories',
    mainCta: 'AllChinaBuy Spreadsheet',
    buyNow: 'Acheter maintenant',
    readMore: 'Lire la suite',
    backHome: "Retour à l'accueil",
    relatedResources: 'Ressources liées',
    exploreResources: 'Explorer plus de ressources AllChinaBuy',
    visitMain: 'Visiter le site principal',
    breadcrumbHome: 'Accueil',
    published: 'Mis à jour',
    articleDisclaimer: "Cet article est fourni à titre informatif uniquement et n'est ni affilié ni approuvé par AllChinaBuy.",
    searchPlaceholder: 'Rechercher produits, vendeurs, guides ou tableurs...',
    categoryLabels: {
      spreadsheet: 'Tableur',
      review: 'Avis',
      shipping: 'Livraison',
      coupon: 'Coupon',
      warehouse: 'Entrepôt',
      trust: 'Confiance',
      qc: 'QC',
      alternatives: 'Alternatives',
      comparison: 'Comparaison',
      guide: 'Guide',
      sellers: 'Vendeurs',
      products: 'Produits',
    },
    footer: {
      about: 'À propos',
      contact: 'Contact',
      editorial: 'Politique éditoriale',
      disclaimer: 'Avertissement',
      affiliate: "Divulgation d'affiliation",
      privacy: 'Politique de confidentialité',
      terms: 'Conditions',
      sitemap: 'Plan du site',
      disclaimerText: "Ce site est une ressource informative indépendante, non affiliée ni officiellement approuvée par AllChinaBuy. Vérifiez toujours les informations actuelles avant tout achat.",
      copyright: 'Tous droits réservés.',
      visitMain: 'Visiter le site principal',
      explore: 'Explorer les ressources',
    },
  },
  da: {
    nav: {
      categories: 'Kategorier',
      guides: 'Guider',
      reviews: 'Anmeldelser',
      shipping: 'Fragt',
      warehouse: 'Lager',
      qc: 'QC',
      faq: 'FAQ',
      cta: 'Udforsk',
      home: 'Hjem',
      shoppingGuide: 'Indkøbsguide',
    },
    categoryLabel: 'Gennemse kategorier',
    mainCta: 'AllChinaBuy Spreadsheet',
    buyNow: 'Køb nu',
    readMore: 'Læs mere',
    backHome: 'Tilbage til forsiden',
    relatedResources: 'Relaterede ressourcer',
    exploreResources: 'Udforsk flere AllChinaBuy-ressourcer',
    visitMain: 'Besøg hovedwebstedet',
    breadcrumbHome: 'Hjem',
    published: 'Opdateret',
    articleDisclaimer: 'Denne artikel er kun til information og er ikke tilknyttet eller godkendt af AllChinaBuy.',
    searchPlaceholder: 'Søg produkter, sælgere, guider eller regneark...',
    categoryLabels: {
      spreadsheet: 'Regneark',
      review: 'Anmeldelse',
      shipping: 'Fragt',
      coupon: 'Kupon',
      warehouse: 'Lager',
      trust: 'Tillid',
      qc: 'QC',
      alternatives: 'Alternativer',
      comparison: 'Sammenligning',
      guide: 'Guide',
      sellers: 'Sælgere',
      products: 'Produkter',
    },
    footer: {
      about: 'Om',
      contact: 'Kontakt',
      editorial: 'Redaktionel politik',
      disclaimer: 'Ansvarsfraskrivelse',
      affiliate: 'Affiliate-oplysning',
      privacy: 'Privatlivspolitik',
      terms: 'Vilkår',
      sitemap: 'Sitemap',
      disclaimerText: 'Dette websted er en uafhængig informationsressource og er ikke tilknyttet eller godkendt af AllChinaBuy. Verificér altid aktuelle oplysninger inden køb.',
      copyright: 'Alle rettigheder forbeholdes.',
      visitMain: 'Besøg hovedwebstedet',
      explore: 'Udforsk ressourcer',
    },
  },
  de: {
    nav: {
      categories: 'Kategorien',
      guides: 'Guides',
      reviews: 'Bewertungen',
      shipping: 'Versand',
      warehouse: 'Lager',
      qc: 'QC',
      faq: 'FAQ',
      cta: 'Entdecken',
      home: 'Startseite',
      shoppingGuide: 'Einkaufsführer',
    },
    categoryLabel: 'Kategorien durchsuchen',
    mainCta: 'AllChinaBuy Spreadsheet',
    buyNow: 'Jetzt kaufen',
    readMore: 'Weiterlesen',
    backHome: 'Zurück zur Startseite',
    relatedResources: 'Verwandte Ressourcen',
    exploreResources: 'Weitere AllChinaBuy-Ressourcen entdecken',
    visitMain: 'Hauptwebseite besuchen',
    breadcrumbHome: 'Start',
    published: 'Aktualisiert',
    articleDisclaimer: 'Dieser Artikel dient nur der Information und ist nicht mit AllChinaBuy verbunden oder von diesem unterstützt.',
    searchPlaceholder: 'Produkte, Verkäufer, Guides oder Spreadsheets suchen...',
    categoryLabels: {
      spreadsheet: 'Spreadsheet',
      review: 'Bewertung',
      shipping: 'Versand',
      coupon: 'Coupon',
      warehouse: 'Lager',
      trust: 'Vertrauen',
      qc: 'QC',
      alternatives: 'Alternativen',
      comparison: 'Vergleich',
      guide: 'Guide',
      sellers: 'Verkäufer',
      products: 'Produkte',
    },
    footer: {
      about: 'Über uns',
      contact: 'Kontakt',
      editorial: 'Redaktionelle Richtlinie',
      disclaimer: 'Haftungsausschluss',
      affiliate: 'Affiliate-Offenlegung',
      privacy: 'Datenschutz',
      terms: 'Bedingungen',
      sitemap: 'Sitemap',
      disclaimerText: 'Diese Website ist eine unabhängige Informationsressource und ist weder mit AllChinaBuy verbunden noch offiziell von diesem unterstützt. Prüfen Sie vor dem Kauf stets die aktuellen Informationen.',
      copyright: 'Alle Rechte vorbehalten.',
      visitMain: 'Hauptwebseite besuchen',
      explore: 'Ressourcen entdecken',
    },
  },
};